import PropTypes from 'prop-types'
import React, { useEffect } from 'react'
import { Box } from 'theme-ui'
import Banner from '~/components/Banner'
import CartFlyout from '~/components/Cart/Flyout'
import Footer from '~/components/Footer'
import LayoutSkipLink from '~/components/Layout/SkipLink'
import Nav from '~/components/Nav'
import NotificationAlertBar from '~/components/Notification/AlertBar'
import NotificationSaleBar from '~/components/Notification/SaleBar'
import NotificationTopBar from '~/components/Notification/TopBar'
import AttentiveScript from '~/components/Attentive/Script'
import ReferralsBubble from '~/components/Referrals/Bubble'
import TunneledNavBar from '~/components/Nav/TunneledNavBar'
import { useCartFlyout } from '~/hooks/components/use-cart-flyout'

const Layout = ({
  banner,
  children,
  includeHeaderFooter = true,
  includeFlyout = true,
  includeNotifications = true,
  linkTunneledLogo = false,
  navJumpCta,
  tunneled = false,
  handleTunneledBack,
}) => {
  const { isCartFlyoutOpen } = useCartFlyout()
  const scrollLockedClass = 'layout-locked'

  useEffect(() => {
    if (includeFlyout && isCartFlyoutOpen && !tunneled) {
      document.body.classList.add(scrollLockedClass)
    } else {
      document.body.classList.remove(scrollLockedClass)
    }
  }, [includeFlyout, isCartFlyoutOpen, tunneled])

  if (!includeHeaderFooter) {
    return <LayoutMain>{children}</LayoutMain>
  }

  // Landing pages with a jump navigation button in place of the menu.
  if (navJumpCta) {
    return (
      <Box>
        <LayoutStickyHeader>
          <Nav jumpCta={navJumpCta} />
        </LayoutStickyHeader>
        <LayoutMain>{children}</LayoutMain>
        <Footer />
        <AttentiveScript />
      </Box>
    )
  }

  // Menuless experiences like Can Club signup, Group Order flow.
  if (tunneled) {
    return (
      <Box>
        <LayoutStickyHeader>
          <TunneledNavBar
            handleBack={handleTunneledBack}
            linkLogo={linkTunneledLogo}
          />
        </LayoutStickyHeader>
        <LayoutMain>{children}</LayoutMain>
      </Box>
    )
  }

  return (
    <Box>
      <LayoutStickyHeader>
        {includeNotifications && <NotificationTopBar />}
        <Nav jumpCta={navJumpCta} />
        {includeNotifications && <NotificationAlertBar />}
      </LayoutStickyHeader>
      {!banner?.hideSaleNotificationBar && <NotificationSaleBar />}
      <LayoutMain>
        {banner && <Banner banner={banner} />}
        {children}
      </LayoutMain>
      <Footer />
      {includeFlyout && <CartFlyout />}
      <AttentiveScript />
      <ReferralsBubble />
    </Box>
  )
}

const LayoutStickyHeader = ({ children }) => (
  <Box
    as="header"
    role="banner"
    sx={{ position: 'sticky', top: '0', zIndex: '100' }}
  >
    <LayoutSkipLink />
    {children}
  </Box>
)

const LayoutMain = ({ children }) => (
  <Box as="main" role="main" id="main">
    {children}
  </Box>
)

Layout.propTypes = {
  children: PropTypes.node.isRequired,
}

export default Layout
